'use client';

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockPart, useCommerceSource, PortalLink } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import {
  buttonStyle,
  errorStyle,
  formStyle,
  inputStyle,
  leadStyle,
  linkRowStyle,
  mutedStyle,
  noticeStyle,
  titleStyle,
} from './accountShared';
import {
  EMPTY_POLICY_DRAFT,
  INSTALLER_POLICY_TYPES,
  formatCover,
  installerErrorKind,
  isoToday,
  policyProblem,
  refusalMessage,
  type InstallerCommerceSource,
  type InstallerPolicyDraft,
  type InstallerPolicyType,
  type InstallerSelfPolicy,
  type InstallerSelfView,
} from './installer';
import {
  AgreementBox,
  InstallerSection,
  PolicyFields,
  fieldStyle,
  inLanguage,
  labelStyle,
  panelStyle,
  readableDate,
  useInstallerProblemText,
  useInstallerWords,
  type Localized,
} from './installerParts';

const { sectionInner } = sharedStyles;

/** Where the token is kept for this tab once it has been read from the address. */
const TOKEN_KEY = 'sorsy.installerLink';

/**
 * An installer's documents page — the `/installer-documents?token=` page every installer email
 * links to — where they keep their agreement and insurance current without signing in.
 *
 * <p>The link's token is read from the page's address once, kept for this tab, and taken out of
 * the address bar, so it does not travel on in a `Referer` or stay in a shared screen's history.
 * It is sent to the shop as a header, never in a URL.
 *
 * <p>The page shows where the installer stands, their policies with each one's expiry and whether
 * it has been checked, the agreement to sign when the shop has published a newer one, and a form
 * to upload a renewed certificate. A link that has expired is said kindly, with the way back: the
 * most recent email, or signing in.
 */
export interface InstallerDocumentsProps {
  title?: Localized;
  intro?: Localized;
  /** What the shop calls its installers, e.g. "fitter". Defaults to "installer". */
  noun?: Localized;
  nounPlural?: Localized;
  /** Offered when the link has expired: a signed-in installer can open this page from their account. */
  signInHref?: string;
  accountHref?: string;
}

type Phase =
  | { kind: 'checking' }
  | { kind: 'invalid' }
  | { kind: 'failed' }
  | { kind: 'ready'; token: string; view: InstallerSelfView };

/** The token from the address, or the one kept for this tab; the address loses it either way. */
function takeToken(): string | null {
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get('token')?.trim() || null;
  if (fromUrl) {
    try {
      window.sessionStorage.setItem(TOKEN_KEY, fromUrl);
    } catch {
      // Storage off: the token still works for as long as this page is open.
    }
    url.searchParams.delete('token');
    try {
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch {
      // A frame that may not rewrite its address keeps it; the page works the same.
    }
    return fromUrl;
  }
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function InstallerDocuments({
  title,
  intro,
  noun,
  nounPlural,
  signInHref = '/sign-in',
  accountHref = '/account',
}: InstallerDocumentsProps) {
  const commerce = useCommerceSource() as InstallerCommerceSource | null;
  const part = useBlockPart();
  const { t, i18n } = useTranslation('site-builder-blocks');
  const words = useInstallerWords(noun, nounPlural);
  const language = i18n?.language;
  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const source = commerce;
    if (!source?.installerSelf) return;
    const token = takeToken();
    if (!token) {
      setPhase({ kind: 'invalid' });
      return;
    }
    void (async () => {
      try {
        const view = await source.installerSelf!(token);
        if (!cancelled) setPhase({ kind: 'ready', token, view });
      } catch (failure) {
        if (cancelled) return;
        const kind = installerErrorKind(failure);
        setPhase({ kind: kind === 'INVALID_LINK' || kind === 'NOT_FOUND' ? 'invalid' : 'failed' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  if (!commerce?.installerSelf || !commerce.signInstallerAgreement || !commerce.renewInstallerPolicy) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="InstallerDocuments"
            adapterName="CommerceSource"
            reason={t('commerce.installer.noAdapter', { ...words })}
          />
        </div>
      </BlockRoot>
    );
  }

  const update = (view: InstallerSelfView, message: string) => {
    setPhase((current) => (current.kind === 'ready' ? { ...current, view } : current));
    setNotice(message);
  };

  let body: ReactNode;
  if (phase.kind === 'checking') {
    body = <p style={mutedStyle}>{t('commerce.installer.documents.loading')}</p>;
  } else if (phase.kind === 'invalid') {
    body = (
      <>
        <p role="status" style={panelStyle}>
          {t('commerce.installer.documents.invalidLink')}
        </p>
        <div style={linkRowStyle}>
          <PortalLink href={signInHref}>{t('commerce.installer.documents.signIn')}</PortalLink>
          <PortalLink href={accountHref}>{t('commerce.installer.documents.goToAccount')}</PortalLink>
        </div>
      </>
    );
  } else if (phase.kind === 'failed') {
    body = (
      <p role="alert" style={errorStyle}>
        {t('commerce.installer.documents.failed')}
      </p>
    );
  } else {
    const { view, token } = phase;
    const status = view.status === 'SUSPENDED' ? 'SUSPENDED' : 'ACTIVE';
    body = (
      <div style={{ display: 'grid', gap: 32 }}>
        <div style={{ display: 'grid', gap: 8 }}>
          <p {...part('intro')} style={leadStyle}>
            {inLanguage(intro, language) || t('commerce.installer.documents.intro')}
          </p>
          <p style={{ margin: 0 }}>
            <strong>{view.businessName}</strong>{' '}
            <span style={pillStyle}>{t(`commerce.installer.documents.status.${status}`)}</span>
          </p>
          <p style={mutedStyle}>{t(`commerce.installer.documents.statusLead.${status}`, { ...words })}</p>
        </div>

        {notice && (
          <p role="status" style={noticeStyle}>
            {notice}
          </p>
        )}

        <InstallerSection title={t('commerce.installer.documents.agreementTitle')}>
          {view.agreementOutOfDate && view.agreement ? (
            <SignAgreement
              commerce={commerce}
              token={token}
              view={view}
              onSigned={(next) =>
                update(next, t('commerce.installer.documents.signed', { version: view.agreement?.version ?? '' }))
              }
              onChanged={(next) => setPhase({ kind: 'ready', token, view: next })}
            />
          ) : (
            <p style={mutedStyle}>
              {view.signedVersion
                ? t('commerce.installer.documents.agreementCurrent', { version: view.signedVersion })
                : t('commerce.installer.documents.agreementNone')}
            </p>
          )}
        </InstallerSection>

        <InstallerSection title={t('commerce.installer.documents.policiesTitle')}>
          {view.policies.length === 0 ? (
            <p style={mutedStyle}>{t('commerce.installer.documents.noPolicies')}</p>
          ) : (
            <ul style={policyListStyle}>
              {view.policies.map((policy) => (
                <PolicyRow key={policy.id} policy={policy} minimum={view.minimumCover[policy.type]} />
              ))}
            </ul>
          )}
        </InstallerSection>

        <RenewPolicy
          commerce={commerce}
          token={token}
          view={view}
          onRenewed={(next) => update(next, t('commerce.installer.documents.renewed'))}
          onInvalid={() => setPhase({ kind: 'invalid' })}
        />
      </div>
    );
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 720 }}>
        <h1 {...part('title')} style={titleStyle}>
          {inLanguage(title, language) || t('commerce.installer.documents.title')}
        </h1>
        {body}
      </div>
    </BlockRoot>
  );
}

function PolicyRow({ policy, minimum }: { policy: InstallerSelfPolicy; minimum?: string }) {
  const { t, i18n } = useTranslation('site-builder-blocks');
  const language = i18n?.language;
  const state = policy.expired && policy.status === 'APPROVED' ? 'EXPIRED' : policy.status;
  return (
    <li style={policyRowStyle}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <strong>{t(`commerce.installer.policies.${policy.type}`)}</strong>
        <span style={state === 'APPROVED' ? pillStyle : warnPillStyle}>
          {t(`commerce.installer.documents.policyStatus.${state}`)}
        </span>
        {policy.required && <span style={pillStyle}>{t('commerce.installer.policy.required')}</span>}
      </div>
      <span style={{ ...mutedStyle, fontSize: 14 }}>
        {[
          policy.insurer,
          policy.policyNumber,
          t('commerce.installer.documents.cover', { amount: formatCover(policy.coverAmount, language) }),
          t(policy.expired ? 'commerce.installer.documents.expired' : 'commerce.installer.documents.expires', {
            date: readableDate(policy.expiresOn, language),
          }),
        ].join(' · ')}
      </span>
      {minimum && Number(policy.coverAmount) < Number(minimum) && (
        <span style={{ fontSize: 14 }}>
          {t('commerce.installer.policy.minimum', { amount: formatCover(minimum, language) })}
        </span>
      )}
      {policy.status === 'REJECTED' && policy.rejectionReason && (
        <span style={{ fontSize: 14 }}>
          {t('commerce.installer.documents.rejectedReason', { reason: policy.rejectionReason })}
        </span>
      )}
    </li>
  );
}

function SignAgreement({
  commerce,
  token,
  view,
  onSigned,
  onChanged,
}: {
  commerce: InstallerCommerceSource;
  token: string;
  view: InstallerSelfView;
  onSigned: (view: InstallerSelfView) => void;
  onChanged: (view: InstallerSelfView) => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const [fullName, setFullName] = useState(view.contactName ?? '');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const agreement = view.agreement!;

  async function sign(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (!fullName.trim() || !agreed) {
      setError(t('commerce.installer.documents.signMissing'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onSigned(
        await commerce.signInstallerAgreement!(token, {
          agreementVersion: agreement.version,
          textHash: agreement.textHash,
          fullName: fullName.trim(),
          agreed,
        }),
      );
    } catch (failure) {
      const kind = installerErrorKind(failure);
      if (kind === 'AGREEMENT_CHANGED') {
        setAgreed(false);
        setError(t('commerce.installer.documents.errors.AGREEMENT_CHANGED'));
        try {
          onChanged(await commerce.installerSelf!(token));
        } catch {
          // The sentence asks them to reload; nothing else to do here.
        }
      } else if (kind === 'INVALID_INPUT') {
        setError(refusalMessage(failure) ?? t('commerce.installer.documents.errors.INVALID_INPUT'));
      } else {
        setError(t(`commerce.installer.documents.errors.${documentsErrorKind(kind)}`));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void sign(event)} noValidate style={{ ...formStyle, maxWidth: 600 }}>
      <p style={{ margin: 0 }}>{t('commerce.installer.documents.agreementOutOfDate')}</p>
      <AgreementBox
        idPrefix="installer-documents"
        agreement={agreement}
        fullName={fullName}
        agreed={agreed}
        onFullName={setFullName}
        onAgreed={setAgreed}
      />
      {error && (
        <p role="alert" style={errorStyle}>
          {error}
        </p>
      )}
      <button type="submit" disabled={busy} style={buttonStyle}>
        {busy ? t('commerce.installer.documents.signing') : t('commerce.installer.documents.sign')}
      </button>
    </form>
  );
}

/** The type most in need of a new certificate: an expired or rejected required one, then any expired one. */
function firstToRenew(view: InstallerSelfView): InstallerPolicyType | '' {
  const needs = (policy: InstallerSelfPolicy) => policy.expired || policy.status === 'REJECTED';
  return (
    view.policies.find((policy) => policy.required && needs(policy))?.type ??
    view.policies.find(needs)?.type ??
    ''
  );
}

function RenewPolicy({
  commerce,
  token,
  view,
  onRenewed,
  onInvalid,
}: {
  commerce: InstallerCommerceSource;
  token: string;
  view: InstallerSelfView;
  onRenewed: (view: InstallerSelfView) => void;
  onInvalid: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const words = useInstallerWords();
  const problemText = useInstallerProblemText(words);
  const [type, setType] = useState<InstallerPolicyType | ''>(() => firstToRenew(view));
  const [draft, setDraft] = useState<InstallerPolicyDraft>({ ...EMPTY_POLICY_DRAFT });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A new key after each upload clears the file input, which React cannot set. */
  const [round, setRound] = useState(0);

  async function renew(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (!type) {
      setError(t('commerce.installer.documents.chooseTypeFirst'));
      return;
    }
    const problem = policyProblem(type, draft, { minimum: view.minimumCover[type], today: isoToday() });
    if (problem) {
      setError(problemText(problem));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await commerce.renewInstallerPolicy!(
        token,
        {
          type,
          insurer: draft.insurer.trim(),
          policyNumber: draft.policyNumber.trim(),
          coverAmount: draft.coverAmount.trim(),
          expiresOn: draft.expiresOn,
        },
        draft.file!,
      );
      setDraft({ ...EMPTY_POLICY_DRAFT });
      setRound((value) => value + 1);
      onRenewed(next);
    } catch (failure) {
      const kind = installerErrorKind(failure);
      if (kind === 'INVALID_LINK') onInvalid();
      else if (kind === 'INVALID_INPUT' || kind === 'INVALID_FILE') {
        setError(refusalMessage(failure) ?? t(`commerce.installer.documents.errors.${kind}`));
      } else setError(t(`commerce.installer.documents.errors.${documentsErrorKind(kind)}`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <InstallerSection
      title={t('commerce.installer.documents.renewTitle')}
      note={t('commerce.installer.documents.renewNote')}
    >
      <form onSubmit={(event) => void renew(event)} noValidate style={{ ...formStyle, maxWidth: 600 }}>
        <div style={fieldStyle}>
          <label htmlFor="installer-renew-type" style={labelStyle}>
            {t('commerce.installer.documents.renewType')}
          </label>
          <select
            id="installer-renew-type"
            value={type}
            onChange={(event) => setType(event.target.value as InstallerPolicyType | '')}
            style={inputStyle}
          >
            <option value="">{t('commerce.installer.documents.chooseType')}</option>
            {INSTALLER_POLICY_TYPES.map((option) => (
              <option key={option} value={option}>
                {t(`commerce.installer.policies.${option}`)}
              </option>
            ))}
          </select>
        </div>
        {type && (
          <PolicyFields
            key={`${type}-${round}`}
            idPrefix="installer-renew"
            type={type}
            value={draft}
            onChange={setDraft}
            minimum={view.minimumCover[type]}
          />
        )}
        {error && (
          <p role="alert" style={errorStyle}>
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} style={buttonStyle}>
          {busy ? t('commerce.installer.documents.renewing') : t('commerce.installer.documents.renew')}
        </button>
      </form>
    </InstallerSection>
  );
}

/** The documents page's own sentences cover these; anything else is "try again". */
function documentsErrorKind(kind: string): string {
  return ['INVALID_LINK', 'AGREEMENT_CHANGED', 'INVALID_INPUT', 'INVALID_FILE', 'RATE_LIMITED', 'NOT_FOUND'].includes(kind)
    ? kind === 'NOT_FOUND'
      ? 'INVALID_LINK'
      : kind
    : 'UNAVAILABLE';
}

const pillStyle = {
  fontSize: 13,
  padding: '2px 10px',
  borderRadius: 999,
  background: 'var(--color-surface-muted, #f3f4f6)',
  whiteSpace: 'nowrap' as const,
};
const warnPillStyle = {
  ...pillStyle,
  background: 'var(--color-warning-50, #fffbeb)',
  color: 'var(--color-warning-700, #b45309)',
};
const policyListStyle = { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 };
const policyRowStyle = {
  display: 'grid',
  gap: 4,
  padding: '12px 14px',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: 'var(--radius-md, 8px)',
};
